import type { EditorThemeClasses } from 'lexical';
import { alpha } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

/**
 * Lexical maps node types to class names; the styling lives in
 * `composerContentSx` so it can read the MUI theme (house rule: no raw hex, no
 * magic px — everything resolves through the theme).
 *
 * Class names are prefixed `teams-composer-` so they cannot collide with the
 * help-article editor's, which is a second Lexical instance in the same app.
 */
export const composerTheme: EditorThemeClasses = {
  link: 'teams-composer-link',
  list: {
    listitem: 'teams-composer-listitem',
    nested: { listitem: 'teams-composer-nested-listitem' },
    ol: 'teams-composer-ol',
    ul: 'teams-composer-ul',
  },
  paragraph: 'teams-composer-paragraph',
  quote: 'teams-composer-quote',
  code: 'teams-composer-code-block',
  text: {
    bold: 'teams-composer-bold',
    code: 'teams-composer-inline-code',
    italic: 'teams-composer-italic',
    strikethrough: 'teams-composer-strikethrough',
    underline: 'teams-composer-underline',
  },
};

/**
 * The editable surface.
 *
 * Every horizontal measure is bounded: the panel is resizable down to 280px and
 * the composer must never be what widens it. Long unbroken strings (a pasted
 * URL) wrap with `overflow-wrap: anywhere` rather than pushing the panel out,
 * and the code block scrolls inside itself.
 */
export const composerContentSx: SxProps<Theme> = {
  minWidth: 0,
  '& .teams-composer-paragraph': { margin: 0, mb: 0.5 },
  '& .teams-composer-paragraph:last-child': { mb: 0 },
  '& .teams-composer-bold': { fontWeight: 700 },
  '& .teams-composer-italic': { fontStyle: 'italic' },
  '& .teams-composer-underline': { textDecoration: 'underline' },
  '& .teams-composer-strikethrough': { textDecoration: 'line-through' },
  '& .teams-composer-link': { color: 'primary.main', textDecoration: 'underline' },
  /**
   * The inserted @mention, deliberately matching `MentionChip` in
   * `TeamsMessageContent` — the same weight and the same `text.primary` tint at
   * the same alpha. Someone should recognise what they are typing as the same
   * object they see in received messages; two different-looking mention styles
   * in one panel would read as two different features.
   *
   * The class is applied by `MentionNode.createDOM`, not through
   * `composerTheme`: Lexical's theme has no slot for a custom node type.
   */
  '& .teams-composer-mention': {
    px: 0.5,
    borderRadius: 0.5,
    fontWeight: 500,
    color: 'inherit',
    bgcolor: (theme: Theme) => alpha(theme.palette.text.primary, 0.1),
    // Token mode already makes it atomic to the editor; this stops a drag
    // selection from making it *look* divisible.
    whiteSpace: 'nowrap',
  },
  '& .teams-composer-ul, & .teams-composer-ol': { pl: 2.5, my: 0.5 },
  '& .teams-composer-listitem': { mb: 0.25 },
  '& .teams-composer-nested-listitem': { listStyleType: 'none' },
  '& .teams-composer-quote': {
    borderLeft: 3,
    borderColor: 'divider',
    pl: 1,
    ml: 0,
    my: 0.5,
    color: 'text.secondary',
  },
  '& .teams-composer-inline-code': {
    fontFamily: 'monospace',
    bgcolor: 'action.hover',
    px: 0.5,
    borderRadius: 0.5,
    fontSize: '0.875em',
  },
  '& .teams-composer-code-block': {
    display: 'block',
    fontFamily: 'monospace',
    bgcolor: 'action.hover',
    p: 1,
    my: 0.5,
    borderRadius: 1,
    fontSize: '0.875em',
    whiteSpace: 'pre-wrap',
    overflowX: 'auto',
  },
};
